import { IConditional } from '../models/common/IConditional';
import { ICondition } from '../models/common/ICondition';
import { getValueFromStringNotation, safe } from '../utils/object';
import { isNullOrUndefined, isUndefined } from '../utils/typeChecks';
import { isArrayLike } from '../utils/isArrayLike';
import IMatchesWithOptions from '../models/helpers/ConditionalHelper/IMatchesWithOptions';
import IValidateConditionOptions from '../models/helpers/ConditionalHelper/IValidateConditionOptions';
import { Query } from '../query/Query';
import { TranslationHelper } from '.';

/**
 * The ConditionalHelper allows for a generic conditional object to be used to determine if certain conditions are met.
 */
export class ConditionalHelper {
	constructor(public conditional: IConditional, public translationHelper?: TranslationHelper) { }
	matchesWithOptions(options: IMatchesWithOptions) {
		let response = false;
		const { conditional = this.conditional, ...validateConditionOptions } = options;
		const { conditions, format } = conditional;
		const conditionValues: any[] = [];
		if (conditions) {
			for (const condition of conditions) {
				const conditionValue = this.validateConditionWithOptions(condition, validateConditionOptions);
				conditionValues.push(conditionValue);
			}
		}
		response = this.applyConditionFormat(conditionValues, conditions, format);
		return response;
	}
	matches(instance: any, conditional?: IConditional) {
		return this.matchesWithOptions({ instance, conditional });
	}
	validateConditionFormat(format: string, keys?: string[]) {
		const validCharacters = ['(', ')', '&', '|', ' ', '{', '}', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
		// Strip out the valid keys
		for (const key of (keys || [])) {
			format = format.split(`{${key}}`).join('');
		}
		const conditionalFormatChars = format.split('');
		let isValid = true;
		for (const char of conditionalFormatChars) {
			isValid = isValid && validCharacters.indexOf(char) > -1;
			if (!isValid) {
				break;
			}
		}
		return isValid;
	}
	orchestrateConditionFormat(conditionResults: boolean[], format: string) {
		if (!format || format === '') {
			format = '';
			for (let i = 1; i <= conditionResults.length; i++) {
				if (i > 1) {
					format += ' && ';
				}
				format += `{${i}}`;
			}
		}
		format = format.toLowerCase();
		format = format.split('and').join('&&');
		format = format.split('or').join('||');
		const singleCharChecks = ['&', '|'];
		const chars = format.split('');
		for (let i = 0; i < chars.length; i++) {
			const currentChar = chars[i];
			for (const singleChar of singleCharChecks) {
				if (currentChar === singleChar) {
					if (i === 0) {
						chars.shift();
						i--;
					}
					else if (i === chars.length - 1) {
						chars.pop();
					}
					else {
						const previousChar = chars[i - 1];
						const nextChar = chars[i + 1];
						if (previousChar !== singleChar && nextChar !== singleChar) {
							chars.splice(i, 0, singleChar);
						}
					}
				}
			}
		}
		while (singleCharChecks.includes(chars[chars.length - 1])) {
			chars.pop();
		}
		format = chars.join('').trim();

		return format;
	}
	applyConditionFormat(conditionResults: boolean[], conditions: ICondition[], format: string) {
		if (!conditionResults || conditionResults.length === 0) {
			return true;
		}
		let workingFormat = this.orchestrateConditionFormat(conditionResults, format);
		const conditionKeys = conditions.filter(condition => !!condition.key).map(condition => condition.key);
		// Check only valid characters used with place holders in workingFormat ({0}, {1} etc.)
		let isValid = this.validateConditionFormat(workingFormat, conditionKeys);
		if (isValid) {
			const indexOffset = workingFormat.indexOf('{0}') > -1 ? 0 : 1;

			for (let i = 0; i < conditionResults.length; i++) {
				// Force the value into a boolean, so as to not let any unwanted values creeping through
				const resultString = (!!conditionResults[i]).toString();
				workingFormat = workingFormat.split(`{${i + indexOffset}}`).join(resultString);
				const condition = conditions && conditions.length > i && conditions[i];
				if (condition) {
					const { key } = condition;
					if (key) {
						workingFormat = workingFormat.split(`{${key}}`).join(resultString);
					}
				}
			}
			// Check no extra place holders in place
			const placeholderBraces = ['{', '}'];
			for (const char of placeholderBraces) {
				isValid = isValid && workingFormat.indexOf(char) === -1;
				if (!isValid) {
					break;
				}
			}
			if (isValid) {
				try {
					// tslint:disable-next-line:no-eval
					isValid = eval(workingFormat);
				}
				catch (ex) {
					isValid = false;
				}
			}
			else {
				console.error(`invalid condition format without place holders: ${workingFormat}`);
			}
		}
		else {
			console.error(`invalid condition format with place holders: ${workingFormat}`);
		}
		return isValid;

	}
	arrayContains(collection: any[], search: any, isCaseSensitive: boolean = false) {
		if (!isNullOrUndefined(collection) && !isNullOrUndefined(search)) {
			return undefined !== (collection).find((item) => {
				let valueString = item.toString();
				let searchString = search.toString();
				if (!isCaseSensitive) {
					valueString = valueString.toLowerCase();
					searchString = searchString.toLowerCase();
				}
				return valueString === searchString;
			});
		}
		return false;
	}
	contains(value: any, search: any, isCaseSensitive: boolean = false) {
		if (!isNullOrUndefined(value) && !isNullOrUndefined(search)) {
			let valueString = value.toString();
			let searchString = search.toString();
			if (!isCaseSensitive) {
				valueString = valueString.toLowerCase();
				searchString = searchString.toLowerCase();
			}
			return valueString.indexOf(searchString) > -1;
		}
		return false;
	}
	startsWith(value: any, search: any, isCaseSensitive: boolean = false) {
		if (!isNullOrUndefined(value) && !isNullOrUndefined(search)) {
			if (!Array.isArray(search)) {
				search = [search];
			}
			return !!(search as any[]).find((s) => {
				let valueString = value.toString();
				let searchString = s.toString();
				if (!isCaseSensitive) {
					valueString = valueString.toLowerCase();
					searchString = searchString.toLowerCase();
				}
				return valueString.indexOf(searchString) === 0;
			});
		}
		return false;
	}
	endsWith(value: any, search: any, isCaseSensitive: boolean = false) {
		if (!isNullOrUndefined(value) && !isNullOrUndefined(search)) {
			if (!Array.isArray(search)) {
				search = [search];
			}
			return !!(search as any[]).find((s) => {
				let valueString = value.toString();
				let searchString = s.toString();
				if (!isCaseSensitive) {
					valueString = valueString.toLowerCase();
					searchString = searchString.toLowerCase();
				}
				return valueString.indexOf(searchString) === valueString.length - searchString.length;
			});
		}
		return false;
	}
	validateConditionWithOptions(condition: ICondition, options: IValidateConditionOptions) {
		if (!condition || typeof condition !== 'object') {
			return false;
		}
		const { field, fieldQuery, operator, isCaseSensitive } = condition;
		const workingOperator = operator ? operator.toLowerCase() : '=';
		let response = false;
		let value: any;
		if (!isUndefined(condition.value)) {
			value = condition.value;
		}
		else if (!isUndefined(condition.valueQuery) && options.executionOptions) {
			const query = new Query(condition.valueQuery);
			value = query.execute(options.executionOptions);
		}

		let translatedValue;
		if (this.translationHelper && ['!=', '=', 'includes', 'excludes', 'in', 'not-in'].indexOf(workingOperator) >= 0) {
			if (Array.isArray(value)) {
				translatedValue = value.map(item => this.translationHelper.translate(item));
			}
			else {
				translatedValue = this.translationHelper.translate(value);
			}
		}

		let fieldValue = options.instance;
		if (field && options.instance) {
			fieldValue = getValueFromStringNotation(field, options.instance);
		}
		else if (fieldQuery && options.executionOptions) {
			const query = new Query(fieldQuery);
			fieldValue = query.execute(options.executionOptions);
		}

		const valueIsDate = this.isDate(value);
		const fieldValueIsDate = this.isDate(fieldValue);

		switch (workingOperator) {
			case '!=':
				response = fieldValue !== value && (translatedValue === undefined || fieldValue !== translatedValue);
				break;
			case '=':
				response = fieldValue === value || (translatedValue !== undefined && fieldValue === translatedValue);
				break;
			case '>':
				if (!valueIsDate || !fieldValueIsDate) {
					response = parseFloat(fieldValue) > parseFloat(value);
				}
				else {
					response = fieldValue > value;
				}
				break;
			case '>=':
				if (!valueIsDate || !fieldValueIsDate) {
					response = parseFloat(fieldValue) >= parseFloat(value);
				}
				else {
					response = fieldValue >= value;
				}
				break;
			case '<':
				if (!valueIsDate || !fieldValueIsDate) {
					response = parseFloat(fieldValue) < parseFloat(value);
				}
				else {
					response = fieldValue < value;
				}

				break;
			case '<=':
				if (!valueIsDate || !fieldValueIsDate) {
					response = parseFloat(fieldValue) <= parseFloat(value);
				}
				else {
					response = fieldValue <= value;
				}
				break;
			case 'includes':
				if (Array.isArray(fieldValue)) {
					response = this.arrayContains(fieldValue, value, isCaseSensitive) || (translatedValue !== undefined && this.arrayContains(fieldValue, translatedValue, isCaseSensitive));
				}
				else {
					response = this.contains(fieldValue, value, isCaseSensitive) || (translatedValue !== undefined && this.contains(fieldValue, translatedValue, isCaseSensitive));
				}
				break;
			case 'excludes':
				if (Array.isArray(fieldValue)) {
					response = !this.arrayContains(fieldValue, value, isCaseSensitive) && (translatedValue === undefined || !this.arrayContains(fieldValue, translatedValue, isCaseSensitive));
				}
				else {
					response = !this.contains(fieldValue, value, isCaseSensitive) && (translatedValue === undefined || !this.contains(fieldValue, translatedValue, isCaseSensitive));
				}
				break;
			case 'in':
				if (isArrayLike(value)) {
					response = this.arrayContains(value, fieldValue, isCaseSensitive) || this.arrayContains(translatedValue, fieldValue, isCaseSensitive);
				}
				break;
			case 'not-in':
				if (isArrayLike(value)) {
					response = !this.arrayContains(value, fieldValue, isCaseSensitive) && !this.arrayContains(translatedValue, fieldValue, isCaseSensitive);
				}
				break;
			case 'defined':
				response = !isUndefined(fieldValue);
				break;
			case 'undefined':
				response = isUndefined(fieldValue);
				break;
			case 'not-null':
				response = fieldValue !== null;
				break;
			case 'null':
				response = fieldValue === null;
				break;
			case 'starts-with':
				response = this.startsWith(fieldValue, value, isCaseSensitive) || this.startsWith(fieldValue, translatedValue, isCaseSensitive);
				break;
			case 'ends-with':
				response = this.endsWith(fieldValue, value, isCaseSensitive) || this.endsWith(fieldValue, translatedValue, isCaseSensitive);
				break;
			case 'not-starts-with':
				response = !this.startsWith(fieldValue, value, isCaseSensitive) && !this.startsWith(fieldValue, translatedValue, isCaseSensitive);
				break;
			case 'not-ends-with':
				response = !this.endsWith(fieldValue, value, isCaseSensitive) && !this.endsWith(fieldValue, translatedValue, isCaseSensitive);
				break;
			default:
				response = false;
				break;
		}
		return response;
	}
	isDate(value: any) {
		return typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/);
	}
	validateCondition(condition: ICondition, instance: any) {
		return this.validateConditionWithOptions(condition, { instance });
	}
}